import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = 'pc.utm_params';

@Injectable({ providedIn: 'root' })
export class UtmTrackingService {
  private readonly doc = inject(DOCUMENT);

  constructor() {
    this.captureFromUrl();
  }

  /**
   * Reads UTM params from the current URL and persists to sessionStorage.
   * Only overwrites if at least one UTM param is present (avoids clearing
   * previously captured params on internal navigations).
   */
  private captureFromUrl(): void {
    const w = this.doc.defaultView;
    if (!w) return;

    const search = new URLSearchParams(w.location.search ?? '');
    const params: UtmParams = {};
    let found = false;

    for (const key of UTM_KEYS) {
      const value = search.get(key);
      if (value) {
        params[key] = value;
        found = true;
      }
    }

    if (found) {
      w.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    }
  }

  /** Returns stored UTM params (empty object if none). */
  getParams(): UtmParams {
    const w = this.doc.defaultView;
    if (!w) return {};
    try {
      const raw = w.sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  /** Returns true if any UTM params were captured. */
  hasParams(): boolean {
    return Object.keys(this.getParams()).length > 0;
  }

  /** Clears stored UTM params (call after successfully sending to auth service). */
  clear(): void {
    const w = this.doc.defaultView;
    w?.sessionStorage.removeItem(STORAGE_KEY);
  }
}
